/**
 * NoteActions - the "..." menu on a note a person typed (docs/plans/crm-report-builder-search-collab-plan.md
 * A1.2, A1.3 steps 1 to 5, B1.4; WP N-7).
 *
 * It offers only what noteActions (noteLogic.ts) says the viewer may do: Edit for the author inside 24
 * hours, History once the note has been edited, Delete for the author inside 24 hours or a CRM admin at
 * any age. The server is the authority (crm.edit_note / crm.delete_note refuse anything else); this menu
 * decides what to offer and renders NOTHING when there is nothing to offer, so the caller can mount it
 * on every note.
 *
 * The component owns the open state, the keyboard (Down opens from the trigger; Up, Down, Home and End
 * move; Escape closes and hands the keys back to the trigger; Tab closes) and the press outside. The
 * caller owns what each item does (X-3 wires NoteEditForm, NoteDeleteDialog and NoteHistoryModal).
 * The panel sits in the Z.menu tier inside the card; the trigger and the rows are 44 px below sm: and
 * compact from sm: up. Escape is handled in the bubble phase on the menu itself and stopped there, so a
 * dialog around the thread (the call workspace) stays open.
 */
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, SyntheticEvent } from 'react';
import { History, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Communication } from '../types';
import { Z } from '../components/ui';
import type { NoteActions as NoteActionFlags } from './types';

export interface NoteActionsProps {
  /** The note the menu belongs to; its id tags the menu (data-note-actions). */
  note: Pick<Communication, 'id'>;
  /** noteActions(note, { userId, isAdmin, now }) from noteLogic.ts. */
  actions: NoteActionFlags;
  /** Shown when actions.canEdit and this is given. */
  onEdit?: () => void;
  /** Shown when actions.canDelete and this is given (last, in red). */
  onDelete?: () => void;
  /** Shown when actions.canViewHistory and this is given. */
  onHistory?: () => void;
  /**
   * Hide the trigger until the pointer is over the card, on devices that can hover (phones and touch
   * screens always show it; keyboard focus shows it too). The card must carry Tailwind's `group`.
   */
  revealOnHover?: boolean;
  /** Extra classes for the wrapper (margins, to sit in the card's header row). */
  className?: string;
}

type ItemKey = 'edit' | 'history' | 'delete';

interface MenuItem {
  key: ItemKey;
  label: string;
  Icon: LucideIcon;
  danger: boolean;
  run: () => void;
}

const stop = (e: SyntheticEvent) => e.stopPropagation();

const TRIGGER_CLS =
  'inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-400 transition duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:h-8 sm:w-8';

const HOVER_HIDE_CLS =
  ' [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100 focus:opacity-100';

const ITEM_CLS =
  'flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm transition-colors duration-150 focus:outline-none sm:min-h-0 sm:py-1.5';

const ITEM_TONE = {
  plain: 'text-gray-700 hover:bg-gray-50 focus:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:bg-gray-700',
  danger: 'text-red-600 hover:bg-red-50 focus:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 dark:focus:bg-red-900/20',
};

export default function NoteActions({
  note,
  actions,
  onEdit,
  onDelete,
  onHistory,
  revealOnHover = false,
  className = '',
}: NoteActionsProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  const items: MenuItem[] = [];
  if (actions.canEdit && onEdit) items.push({ key: 'edit', label: 'Edit', Icon: Pencil, danger: false, run: onEdit });
  if (actions.canViewHistory && onHistory) {
    items.push({ key: 'history', label: 'History', Icon: History, danger: false, run: onHistory });
  }
  if (actions.canDelete && onDelete) items.push({ key: 'delete', label: 'Delete', Icon: Trash2, danger: true, run: onDelete });
  const count = items.length;

  // A press anywhere outside the menu closes it (bubble phase, like every CRM popover).
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  // Opening puts the keyboard on the first item.
  useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  // The 24-hour window can end while the menu is open: with nothing left to offer it closes.
  useEffect(() => {
    if (!count) setOpen(false);
  }, [count]);

  if (!count) return null;

  const focusAt = (index: number) => {
    const els = itemRefs.current.slice(0, count).filter((el): el is HTMLButtonElement => Boolean(el));
    if (!els.length) return;
    els[((index % els.length) + els.length) % els.length].focus();
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const at = itemRefs.current.slice(0, count).findIndex((el) => el === document.activeElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusAt(at < 0 ? 0 : at + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusAt(at < 0 ? count - 1 : at - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusAt(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusAt(count - 1);
    } else if (e.key === 'Tab') {
      // Close, and let Tab move on from the trigger rather than from an item that is going away.
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const onTriggerKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const choose = (item: MenuItem) => {
    setOpen(false);
    item.run();
  };

  const hoverCls = revealOnHover && !open ? HOVER_HIDE_CLS : '';

  return (
    <div
      ref={wrapRef}
      data-note-actions={note.id}
      className={`relative inline-flex shrink-0 ${className}`}
      onClick={stop}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        aria-label="Note actions"
        title="Note actions"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className={`${TRIGGER_CLS}${hoverCls}`}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Note actions"
          onKeyDown={onMenuKeyDown}
          className={`absolute right-0 top-full mt-1 ${Z.menu} w-40 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800`}
        >
          {items.map((item, index) => (
            <Fragment key={item.key}>
              {item.key === 'delete' && index > 0 && (
                <div role="separator" className="my-1 border-t border-gray-100 dark:border-gray-700" />
              )}
              <button
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                type="button"
                role="menuitem"
                data-note-action={item.key}
                onClick={() => choose(item)}
                className={`${ITEM_CLS} ${item.danger ? ITEM_TONE.danger : ITEM_TONE.plain}`}
              >
                <item.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {item.label}
              </button>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
